import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import { Card, Button, Badge, Progress, Modal } from '../components/ui';
import { 
  Camera, 
  Play, 
  Plus,
  Calendar,
  TrendingUp,
  Loader2,
  Image,
  ChevronLeft,
  ChevronRight,
  Download,
  Share2,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TimeLapse = () => {
  const { t } = useTranslation();
  const { tattooId } = useParams();
  const [tattoo, setTattoo] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [previousPhoto, setPreviousPhoto] = useState(null);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (tattooId) {
      fetchData();
    }
  }, [tattooId]);

  useEffect(() => {
    let interval;
    if (isPlaying && photos.length > 1) {
      interval = setInterval(() => {
        setCurrentPhotoIndex(prev => (prev + 1) % photos.length);
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isPlaying, photos.length]);

  const fetchData = async () => {
    try {
      const [tattooRes, photosRes] = await Promise.all([
        fetch(`${API_URL}/api/tattoos/${tattooId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/tattoos/${tattooId}/healing-photos`, { credentials: 'include' })
      ]);
      
      if (tattooRes.ok) {
        const data = await tattooRes.json();
        setTattoo(data);
        setAnalysis(data.last_healing_analysis);
      }
      
      if (photosRes.ok) {
        const data = await photosRes.json();
        setPhotos(data);
        if (data.length > 0) {
          setPreviousPhoto(data[data.length - 1].photo_url);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // For now, create a local URL (in production, upload to Cloudinary first)
    const reader = new FileReader();
    reader.onloadend = async () => {
      // Here you would upload to Cloudinary and get the URL
      // For demo, we'll just show the preview
      const newDay = photos.length + 1;
      
      try {
        const response = await fetch(`${API_URL}/api/tattoos/${tattooId}/healing-photos`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            photo_url: reader.result, // In production: Cloudinary URL
            day_number: newDay
          }),
        });

        if (response.ok) {
          fetchData();
          setShowUploadModal(false);
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAnalyze = async () => {
    if (photos.length < 3) {
      alert('Time-lapse analizi için en az 3 fotoğraf gerekli');
      return;
    }

    setAnalyzing(true);
    
    try {
      // Convert photos to base64 (in production, you'd fetch from Cloudinary)
      const images = photos.slice(-7).map(p => p.photo_url.split(',')[1] || '');
      
      const response = await fetch(`${API_URL}/api/tattoos/${tattooId}/analyze-timelapse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ images }),
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysis(data.analysis);
      }
    } catch (error) {
      console.error('Analysis error:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'İyileşiyor': return 'text-green-500';
      case 'Stabil': return 'text-yellow-500';
      case 'Kötüleşiyor': return 'text-red-500';
      default: return 'text-primary';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Badge className="mb-4">
            <Calendar className="w-3 h-3 mr-1" />
            İyileşme Takibi
          </Badge>
          <h1 className="font-heading text-3xl mb-2" data-testid="timelapse-title">
            TIME-LAPSE <span className="text-primary">İYİLEŞME</span>
          </h1>
          <p className="text-muted-foreground">
            {tattoo?.name} - Gün {photos.length}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Photo Area */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              {/* Photo Viewer */}
              <div className="relative aspect-video bg-muted rounded-sm overflow-hidden mb-4">
                {photos.length > 0 ? (
                  <>
                    <img 
                      src={photos[currentPhotoIndex]?.photo_url}
                      alt={`Day ${photos[currentPhotoIndex]?.day_number}`}
                      className="w-full h-full object-contain"
                    />
                    <div className="absolute bottom-4 left-4 bg-black/60 px-3 py-1 rounded-sm">
                      <span className="text-sm font-medium">
                        Gün {photos[currentPhotoIndex]?.day_number}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <Image className="w-16 h-16 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Henüz fotoğraf yok</p>
                  </div>
                )}
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => setCurrentPhotoIndex(prev => Math.max(0, prev - 1))}
                    disabled={photos.length <= 1 || currentPhotoIndex === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant={isPlaying ? 'default' : 'outline'}
                    onClick={() => setIsPlaying(!isPlaying)}
                    disabled={photos.length <= 1}
                  >
                    <Play className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => setCurrentPhotoIndex(prev => Math.min(photos.length - 1, prev + 1))}
                    disabled={photos.length <= 1 || currentPhotoIndex === photos.length - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowUploadModal(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Fotoğraf Ekle
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAnalyze}
                    disabled={photos.length < 3 || analyzing}
                  >
                    {analyzing ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <TrendingUp className="w-4 h-4 mr-2" />
                    )}
                    Analiz Et
                  </Button>
                </div>
              </div>

              {/* Timeline */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {photos.map((photo, index) => (
                  <button
                    key={photo.photo_id}
                    onClick={() => setCurrentPhotoIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 rounded-sm overflow-hidden border-2 transition-colors ${
                      index === currentPhotoIndex ? 'border-primary' : 'border-transparent'
                    }`}
                  >
                    <img 
                      src={photo.photo_url}
                      alt={`Day ${photo.day_number}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </Card>
          </div>

          {/* Analysis Panel */}
          <div className="space-y-6">
            {/* Healing Score */}
            <Card className="p-6">
              <h3 className="font-heading text-lg mb-4">İyileşme Skoru</h3>
              {analysis ? (
                <>
                  <div className="text-center mb-4">
                    <span className="text-5xl font-heading text-primary">
                      {analysis.healing_score || 75}
                    </span>
                    <span className="text-muted-foreground">/100</span>
                  </div>
                  <Progress value={analysis.healing_score || 75} className="mb-4" />
                  <div className="flex items-center justify-center gap-2">
                    <Badge variant={
                      analysis.healing_status === 'Mükemmel' ? 'success' :
                      analysis.healing_status === 'Normal' ? 'default' :
                      'warning'
                    }>
                      {analysis.healing_status || 'Normal'}
                    </Badge>
                    <span className={getTrendColor(analysis.trend)}>
                      {analysis.trend || 'Stabil'}
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">
                    En az 3 fotoğraf yükleyip analiz edin
                  </p>
                </div>
              )}
            </Card>

            {/* Recommendations */}
            {analysis?.recommendations && (
              <Card className="p-6">
                <h3 className="font-heading text-lg mb-4">Öneriler</h3>
                <ul className="space-y-2">
                  {analysis.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Concerns */}
            {analysis?.concerns && analysis.concerns.length > 0 && (
              <Card className="p-6 border-warning/30">
                <h3 className="font-heading text-lg mb-4 text-yellow-500">Dikkat Edilmesi Gerekenler</h3>
                <ul className="space-y-2">
                  {analysis.concerns.map((concern, i) => (
                    <li key={i} className="text-sm text-muted-foreground">
                      • {concern}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Retouch Prediction */}
            {analysis?.retouch_prediction && (
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Rötuş Tahmini</span>
                  <Badge variant={
                    analysis.retouch_prediction === 'Gerekli Değil' ? 'success' : 'warning'
                  }>
                    {analysis.retouch_prediction}
                  </Badge>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Upload Modal with Ghost Overlay */}
        <Modal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          title="Günlük Fotoğraf Ekle"
        >
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Dövmenizi her gün aynı açıdan çekin. Önceki fotoğrafınız referans olarak gösterilecek.
            </p>
            
            {/* Ghost Overlay Preview */}
            {previousPhoto && (
              <div className="relative aspect-video bg-muted rounded-sm overflow-hidden">
                <img 
                  src={previousPhoto}
                  alt="Previous"
                  className="w-full h-full object-contain opacity-30"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <Camera className="w-12 h-12 text-primary mx-auto mb-2" />
                    <p className="text-sm">Önceki fotoğrafı hizalayın</p>
                  </div>
                </div>
              </div>
            )}

            <Button 
              className="w-full"
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera className="w-4 h-4 mr-2" />
              Fotoğraf Seç
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />

            <p className="text-xs text-muted-foreground text-center">
              İpucu: Gün ışığında, aynı mesafeden ve açıdan çekim yapın
            </p>
          </div>
        </Modal>
      </main>
    </div>
  );
};

export default TimeLapse;
